import type { Locale } from "@/i18n/config";
import { localizedText } from "@/i18n/text";

/** Allmän support för studenter. Personuppgiftsfrågor går till privacy-adressen. */
export const supportEmail = "support@campuslyan.se";
export const privacyEmail = "privacy@campuslyan.se";

type LocalizedCopy = { sv: string; en: string };

type FaqLinkSource = LocalizedCopy & { href: string };

type FaqEntrySource = {
  id: string;
  question: LocalizedCopy;
  /** Ett stycke per element. Slås ihop till en sträng i FAQPage-JSON-LD:n. */
  answer: LocalizedCopy[];
  links?: FaqLinkSource[];
};

type FaqCategorySource = {
  id: string;
  title: LocalizedCopy;
  description: LocalizedCopy;
  items: FaqEntrySource[];
};

export type FaqLink = { href: string; label: string };

export type FaqEntry = {
  id: string;
  question: string;
  answer: string[];
  links: FaqLink[];
};

export type FaqCategory = {
  id: string;
  title: string;
  description: string;
  items: FaqEntry[];
};

/**
 * Innehållet i /faq. Skrivet för studenter och avsiktligt förankrat i vad
 * plattformen faktiskt gör — svar som lovar funktioner vi inte har byggt
 * skapar fler supportärenden än de löser.
 */
const faqSource: FaqCategorySource[] = [
  {
    id: "komma-igang",
    title: { sv: "Komma igång", en: "Getting started" },
    description: {
      sv: "Vad CampusLyan är, vad det kostar och hur du skaffar ett konto.",
      en: "What CampusLyan is, what it costs and how to get an account.",
    },
    items: [
      {
        id: "vad-ar-campuslyan",
        question: { sv: "Vad är CampusLyan?", en: "What is CampusLyan?" },
        answer: [
          {
            sv: "CampusLyan samlar studentbostäder, bostadsköer och bostadsföretag på ett och samma ställe. Du söker lediga bostäder i hela Sverige, ställer dig i kö hos flera bostadsföretag och följer alla dina ansökningar från samma konto.",
            en: "CampusLyan gathers student homes, housing queues and housing companies in one place. You search available homes across Sweden, join queues with several housing companies and follow all your applications from the same account.",
          },
          {
            sv: "Vi är varken hyresvärd eller bostadsförmedling — vi är plattformen som gör det enklare att hitta rätt. Beslutet om vem som får en bostad fattas alltid av bostadsföretaget.",
            en: "We are neither a landlord nor a letting agency — we are the platform that makes it easier to find the right home. The decision on who gets a home is always made by the housing company.",
          },
        ],
        links: [
          { href: "/housing", sv: "Sök bostäder", en: "Search homes" },
          { href: "/all-queues", sv: "Alla köer", en: "All queues" },
        ],
      },
      {
        id: "kostar-det-nagot",
        question: {
          sv: "Kostar det något för mig som student?",
          en: "Does it cost anything for me as a student?",
        },
        answer: [
          {
            sv: "Nej. Att skapa konto, söka bostäder, ställa dig i köer och skicka ansökningar via CampusLyan är helt gratis. Inga dolda avgifter, inget abonnemang och inget kort som behöver registreras.",
            en: "No. Creating an account, searching for homes, joining queues and sending applications through CampusLyan is completely free. No hidden fees, no subscription and no card to register.",
          },
          {
            sv: "Enskilda bostadsföretag kan ha egna köavgifter eller villkor. Det framgår i så fall hos företaget — men CampusLyan tar aldrig betalt av dig.",
            en: "Individual housing companies may charge their own queue fees or set their own terms. That is stated by the company — but CampusLyan never charges you.",
          },
        ],
      },
      {
        id: "skapa-konto",
        question: { sv: "Hur skapar jag ett konto?", en: "How do I create an account?" },
        answer: [
          {
            sv: "Klicka på Skapa konto, fyll i din e-postadress och ett lösenord — eller registrera dig med Google. Kontot är igång direkt och du kan börja titta runt med en gång.",
            en: "Click Create account, enter your email address and a password — or sign up with Google. The account is ready immediately and you can start browsing right away.",
          },
          {
            sv: "Lösenordet behöver vara minst 8 tecken och innehålla liten bokstav, stor bokstav, siffra och specialtecken.",
            en: "The password must be at least 8 characters and contain a lowercase letter, an uppercase letter, a number and a special character.",
          },
          {
            sv: "För att kunna ställa dig i kö eller ansöka om en bostad verifierar du sedan kontot — det tar någon minut och är också gratis.",
            en: "To join a queue or apply for a home you then verify the account — it takes a minute and is also free.",
          },
        ],
        links: [{ href: "/register", sv: "Skapa konto", en: "Create account" }],
      },
      {
        id: "utan-inloggning",
        question: {
          sv: "Vad kan jag göra utan att logga in?",
          en: "What can I do without logging in?",
        },
        answer: [
          {
            sv: "Du kan söka och läsa alla annonser, se vilka bostadsföretag och köer som finns, utforska städer och läsa om företagen bakom bostäderna.",
            en: "You can search and read every listing, see which housing companies and queues exist, explore cities and read about the companies behind the homes.",
          },
          {
            sv: "För att spara bostäder, ställa dig i kö, ansöka och få notiser behöver du ett konto.",
            en: "To save homes, join queues, apply and receive notifications you need an account.",
          },
        ],
      },
      {
        id: "snabbregistrerad-vs-verifierad",
        question: {
          sv: "Vad är skillnaden mellan ett snabbregistrerat och ett verifierat konto?",
          en: "What is the difference between a quick-register and a verified account?",
        },
        answer: [
          {
            sv: "Ett snabbregistrerat konto — e-post och lösenord — låter dig utforska plattformen och spana in utbudet. Ett verifierat studentkonto låser upp köer, ansökningar, sparade bostäder, dokument och notiser.",
            en: "A quick-register account — email and password — lets you explore the platform and browse what is available. A verified student account unlocks queues, applications, saved homes, documents and notifications.",
          },
          {
            sv: "Uppgraderingen är gratis. Du behåller samma konto och behöver inte registrera dig på nytt — du verifierar bara din identitet.",
            en: "Upgrading is free. You keep the same account and do not need to sign up again — you only verify your identity.",
          },
        ],
        links: [{ href: "/account", sv: "Mitt konto", en: "My account" }],
      },
      {
        id: "maste-jag-vara-antagen",
        question: {
          sv: "Måste jag vara antagen till en utbildning?",
          en: "Do I need to be admitted to a programme?",
        },
        answer: [
          {
            sv: "Nej, du behöver inget antagningsbesked för att skapa konto, söka bostad eller ställa dig i kö hos oss. Tvärtom lönar det sig att ställa sig i köerna långt innan du vet var du ska plugga.",
            en: "No, you do not need an admission decision to create an account, search for homes or join queues with us. On the contrary, it pays off to join the queues long before you know where you will study.",
          },
          {
            sv: "Däremot kan enskilda bostadsföretag kräva studieintyg eller antagningsbevis innan de erbjuder dig en bostad. Det står i annonsens kravprofil. Enligt användarvillkoren behöver du normalt vara minst 18 år för att ha ett konto.",
            en: "Individual housing companies may, however, require proof of studies or an admission decision before they offer you a home. That is stated in the listing's requirements profile. Under the terms of use you generally need to be at least 18 to hold an account.",
          },
        ],
        links: [
          { href: "/terms-of-service", sv: "Användarvillkor", en: "Terms of use" },
        ],
      },
    ],
  },
  {
    id: "konto-och-verifiering",
    title: { sv: "Konto och verifiering", en: "Account and verification" },
    description: {
      sv: "Verifiering med e-legitimation, e-post, lösenord och dina inställningar.",
      en: "Verification with an e-ID, email, passwords and your settings.",
    },
    items: [
      {
        id: "varfor-verifiering",
        question: { sv: "Varför måste jag verifiera mig?", en: "Why do I have to verify myself?" },
        answer: [
          {
            sv: "Bostadsföretagen behöver veta att personen bakom en ansökan finns på riktigt. Verifieringen skyddar också dig: ingen annan kan söka bostäder i ditt namn, och köplatserna går till verkliga sökande i stället för till påhittade konton.",
            en: "Housing companies need to know that the person behind an application is real. Verification also protects you: nobody else can apply for homes in your name, and queue positions go to real applicants instead of made-up accounts.",
          },
          {
            sv: "Därför krävs både verifierad e-postadress och verifierad identitet för att ställa dig i kö och för att ansöka om en bostad.",
            en: "That is why both a verified email address and a verified identity are required to join a queue and to apply for a home.",
          },
        ],
      },
      {
        id: "verifiera-identitet",
        question: {
          sv: "Hur verifierar jag min identitet?",
          en: "How do I verify my identity?",
        },
        answer: [
          {
            sv: "Gå till Mitt konto och välj Verifiera konto. Sitter du vid datorn skannar du QR-koden med Freja-appen; är du redan i mobilen öppnas appen direkt. Godkänn begäran i appen så uppdateras kontot automatiskt inom några sekunder.",
            en: "Go to My account and choose Verify account. On a computer you scan the QR code with the Freja app; on your phone the app opens directly. Approve the request in the app and the account updates automatically within seconds.",
          },
        ],
        links: [{ href: "/account", sv: "Verifiera kontot", en: "Verify the account" }],
      },
      {
        id: "skaffa-freja",
        question: { sv: "Jag har inte Freja — hur skaffar jag det?", en: "I do not have Freja — how do I get it?" },
        answer: [
          {
            sv: "Ladda ner Freja eID-appen, skanna ditt pass eller nationella id-kort i appen och följ stegen. Det tar ungefär fem minuter och kostar ingenting.",
            en: "Download the Freja eID app, scan your passport or national ID card in the app and follow the steps. It takes about five minutes and costs nothing.",
          },
          {
            sv: "När Freja är klart återvänder du hit och verifierar kontot. Du behöver bara göra det en gång.",
            en: "Once Freja is ready you come back here and verify the account. You only need to do it once.",
          },
        ],
      },
      {
        id: "bankid",
        question: { sv: "Kan jag verifiera mig med BankID?", en: "Can I verify with BankID?" },
        answer: [
          {
            sv: "BankID är på väg. Så länge det står Kommer snart går det inte att välja — men när det aktiveras dyker det upp som ett vanligt alternativ vid verifieringen utan att du behöver göra något.",
            en: "BankID is on its way. As long as it says Coming soon it cannot be selected — but once it goes live it simply appears as a normal option during verification, with nothing for you to do.",
          },
          {
            sv: "Fram till dess är Freja vägen in.",
            en: "Until then, Freja is the way in.",
          },
        ],
      },
      {
        id: "verifiera-epost",
        question: {
          sv: "Hur verifierar jag min e-postadress?",
          en: "How do I verify my email address?",
        },
        answer: [
          {
            sv: "Under Mitt konto finns en verifieringsknapp vid din e-postadress. Vi skickar ett mail med en länk — klicka på länken så är adressen verifierad.",
            en: "In My account there is a verification button next to your email address. We send you an email with a link — click it and the address is verified.",
          },
          {
            sv: "Hittar du inte mailet, titta i skräpposten och kontrollera att adressen är rätt stavad innan du skickar om det.",
            en: "If you cannot find the email, check your spam folder and make sure the address is spelled correctly before sending it again.",
          },
        ],
      },
      {
        id: "verifiering-misslyckades",
        question: {
          sv: "Verifieringen misslyckades eller avbröts — vad gör jag?",
          en: "The verification failed or was cancelled — what do I do?",
        },
        answer: [
          {
            sv: "De vanligaste orsakerna är att begäran hann löpa ut, att den avbröts i appen, eller att identiteten inte matchade kontot — till exempel om samma person redan har ett konto hos oss.",
            en: "The most common reasons are that the request expired, that it was cancelled in the app, or that the identity did not match the account — for example if the same person already has an account with us.",
          },
          {
            sv: `Ingenting sparas när något går fel, så du kan starta om direkt. Fortsätter det att strula: mejla ${supportEmail} så hjälper vi dig.`,
            en: `Nothing is saved when something goes wrong, so you can simply start over. If it keeps failing, email ${supportEmail} and we will help you.`,
          },
        ],
      },
      {
        id: "glomt-losenord",
        question: { sv: "Jag har glömt mitt lösenord.", en: "I have forgotten my password." },
        answer: [
          {
            sv: "Välj Glömt lösenord på inloggningssidan och ange din e-postadress, så skickar vi en återställningslänk. Är du redan inloggad startar du samma flöde från Mitt konto.",
            en: "Choose Forgot password on the login page and enter your email address, and we will send a reset link. If you are already logged in you start the same flow from My account.",
          },
        ],
        links: [
          { href: "/forgot-password", sv: "Återställ lösenord", en: "Reset password" },
          { href: "/login", sv: "Logga in", en: "Log in" },
        ],
      },
      {
        id: "flera-konton",
        question: { sv: "Kan jag ha flera konton?", en: "Can I have several accounts?" },
        answer: [
          {
            sv: "Nej. Kontot är personligt och varje person får bara ha ett konto — det är en förutsättning för att köer och ansökningar ska vara rättvisa.",
            en: "No. The account is personal and each person may only hold one account — it is a precondition for queues and applications to be fair.",
          },
          {
            sv: "Identitetsverifieringen fångar upp om samma identitet försöker verifiera ett andra konto.",
            en: "Identity verification catches it if the same identity tries to verify a second account.",
          },
        ],
      },
      {
        id: "andra-uppgifter",
        question: { sv: "Hur ändrar jag mina uppgifter?", en: "How do I change my details?" },
        answer: [
          {
            sv: "Namn, telefonnummer och e-postadress ändrar du under Mitt konto. Där byter du också lösenord och ser status på din verifiering.",
            en: "You change your name, phone number and email address in My account. That is also where you change your password and see the status of your verification.",
          },
        ],
        links: [{ href: "/account", sv: "Mitt konto", en: "My account" }],
      },
      {
        id: "mina-intressen",
        question: {
          sv: "Vad används Mina intressen till?",
          en: "What are My interests used for?",
        },
        answer: [
          {
            sv: "Där markerar du de studentstäder och lärosäten som är aktuella för dig, och du kan välja flera av varje. Det ersätter den gamla inställningen med en enda hemstad och skola.",
            en: "There you mark the student cities and universities that are relevant to you, and you can pick several of each. It replaces the old setting with a single home city and school.",
          },
          {
            sv: "Valen begränsar ingenting — du kan alltid söka bostad och ställa dig i kö var som helst i landet, och du kan ändra dina intressen när du vill.",
            en: "The choices do not limit anything — you can always search for homes and join queues anywhere in the country, and you can change your interests whenever you like.",
          },
        ],
      },
      {
        id: "radera-konto",
        question: { sv: "Hur raderar jag mitt konto?", en: "How do I delete my account?" },
        answer: [
          {
            sv: "Under Mitt konto finns Radera konto längst ner. Du får bekräfta i en dialog innan något händer.",
            en: "At the bottom of My account you will find Delete account. You confirm in a dialog before anything happens.",
          },
          {
            sv: "Tänk på att köplatser, kötid, ansökningar och sparade bostäder försvinner samtidigt och inte går att få tillbaka.",
            en: "Keep in mind that queue positions, queue time, applications and saved homes disappear at the same time and cannot be restored.",
          },
        ],
      },
    ],
  },
  {
    id: "bostadskoer",
    title: { sv: "Bostadsköer", en: "Housing queues" },
    description: {
      sv: "När kö krävs, hur köerna fungerar, hur kötid räknas och vad som händer när du lämnar en kö.",
      en: "When a queue is required, how the queues work, how queue time is counted and what happens when you leave one.",
    },
    items: [
      {
        id: "maste-jag-sta-i-ko",
        question: {
          sv: "Måste jag stå i kö för att söka bostad?",
          en: "Do I need to be in a queue to apply for a home?",
        },
        answer: [
          {
            sv: "Inte alltid. Bostäder märkta Express går först till kvarn — då ansöker du direkt utan att stå i någon kö alls. Andra kräver att du står i bostadsföretagets bostadskö, och där är det kötiden som avgör.",
            en: "Not always. Homes marked Express are first come, first served — you apply directly without being in any queue at all. Others require you to be in the housing company's queue, and there it is queue time that decides.",
          },
          {
            sv: "Express-bostäder är märkta med en gul Express-symbol, både i sökresultatet och på annonssidan. Kräver bostaden i stället kö ser du på annonssidan om du redan står i den, och kan gå med direkt därifrån.",
            en: "Express homes carry a yellow Express mark, both in the search results and on the listing page. If the home requires a queue instead, the listing page shows whether you are already in it, and lets you join straight from there.",
          },
          {
            sv: "Du kan också filtrera bostadssöket på ansökningstyp om du bara vill se det ena eller det andra.",
            en: "You can also filter the housing search by application type if you only want to see one or the other.",
          },
        ],
        links: [{ href: "/housing", sv: "Sök bostäder", en: "Search homes" }],
      },
      {
        id: "hur-fungerar-koer",
        question: { sv: "Hur fungerar bostadsköer på CampusLyan?", en: "How do housing queues work on CampusLyan?" },
        answer: [
          {
            sv: "Många bostadsföretag har en egen bostadskö — men inte alla. Under Alla köer ser du de företag som har en kö, vilka städer de finns i och vad som gäller hos dem. Du ställer dig i kön direkt från företagets sida.",
            en: "Many housing companies run a housing queue of their own — but not all of them. Under All queues you see the companies that have one, the cities they operate in and what applies with them. You join the queue straight from the company's page.",
          },
          {
            sv: "Längst ned på samma sida listar vi företagen som inte har någon kö. Deras bostäder söker du direkt på annonsen.",
            en: "At the bottom of the same page we list the companies that run no queue. You apply for their homes directly on the listing.",
          },
          {
            sv: "Under Mina köer samlas alla köer du står i, med kötiden du har samlat i var och en.",
            en: "My queues gathers every queue you are in, along with the queue time you have built up in each one.",
          },
        ],
        links: [
          { href: "/all-queues", sv: "Alla köer", en: "All queues" },
          { href: "/queues", sv: "Mina köer", en: "My queues" },
        ],
      },
      {
        id: "hur-manga-koer",
        question: { sv: "Hur många köer får jag stå i?", en: "How many queues can I be in?" },
        answer: [
          {
            sv: "Så många du vill. Det finns ingen gräns hos oss och det kostar ingenting — och ju fler köer du står i, desto större är chansen att rätt bostad dyker upp.",
            en: "As many as you like. There is no limit with us and it costs nothing — and the more queues you are in, the greater the chance that the right home turns up.",
          },
          {
            sv: "Kötiden räknas separat i varje kö.",
            en: "Queue time is counted separately in each queue.",
          },
        ],
      },
      {
        id: "nar-borjar-kotiden",
        question: { sv: "När börjar min kötid räknas?", en: "When does my queue time start counting?" },
        answer: [
          {
            sv: "Från den dag du går med i kön. Kötiden är därför det enklaste sättet att förbättra dina chanser — ställ dig i köerna långt innan du faktiskt behöver flytta, även om du inte söker något just nu.",
            en: "From the day you join the queue. Queue time is therefore the simplest way to improve your chances — join the queues long before you actually need to move, even if you are not applying for anything right now.",
          },
          {
            sv: "Har du redan kötid hos företaget sedan tidigare, från deras eget kösystem, följer den med när du går med via CampusLyan. Du börjar alltså inte om från noll bara för att du hittade dem här.",
            en: "If you already have queue time with the company from before, in their own queue system, it comes along when you join through CampusLyan. You do not start over from zero just because you found them here.",
          },
        ],
      },
      {
        id: "lamna-ko",
        question: { sv: "Vad händer om jag lämnar en kö?", en: "What happens if I leave a queue?" },
        answer: [
          {
            sv: "Din plats och hela din kötid i just den kön försvinner direkt och går inte att få tillbaka. Går du med igen börjar du om från noll dagar.",
            en: "Your position and all of your queue time in that particular queue disappear immediately and cannot be restored. If you rejoin, you start over from zero days.",
          },
          {
            sv: "Övriga köer påverkas inte. Vi frågar alltid en extra gång innan du lämnar, just för att steget inte går att ångra.",
            en: "Your other queues are unaffected. We always ask once more before you leave, precisely because the step cannot be undone.",
          },
        ],
      },
      {
        id: "kan-inte-ko",
        question: { sv: "Varför kan jag inte ställa mig i kö?", en: "Why can't I join a queue?" },
        answer: [
          {
            sv: "Oftast för att kontot inte är färdigt. Du behöver ett verifierat studentkonto med både verifierad e-postadress och verifierad identitet. Är du snabbregistrerad skickas du vidare till verifieringen när du klickar.",
            en: "Usually because the account is not complete. You need a verified student account with both a verified email address and a verified identity. If you are quick-registered you are sent on to verification when you click.",
          },
          {
            sv: "Det kan också vara kön som är pausad eller stängd hos företaget just då — det syns på köns status.",
            en: "It can also be that the company has paused or closed the queue at that moment — the queue's status shows this.",
          },
          {
            sv: "Och alla företag har ingen kö. Saknas den finns det ingenting att ställa sig i — deras bostäder söker du i stället direkt på annonsen.",
            en: "And not every company has a queue. Where there is none there is nothing to join — you apply for their homes directly on the listing instead.",
          },
        ],
      },
      {
        id: "hur-lang-kotid",
        question: {
          sv: "Hur lång kötid behöver jag för att få en bostad?",
          en: "How much queue time do I need to get a home?",
        },
        answer: [
          {
            sv: "Det varierar kraftigt mellan städer, företag och bostadstyper, och det är företaget som äger sin kö och sina regler — vi kan inte lova någon väntetid. Vissa köer visar en ungefärlig väntetid.",
            en: "It varies a great deal between cities, companies and types of home, and each company owns its queue and its rules — we cannot promise a waiting time. Some queues show an approximate wait.",
          },
          {
            sv: "Det som faktiskt hjälper: stå i flera köer, börja tidigt och sök aktivt på de bostäder som dyker upp.",
            en: "What actually helps: be in several queues, start early and actively apply for the homes that appear.",
          },
        ],
      },
      {
        id: "flytta-kotid",
        question: {
          sv: "Kan jag flytta över kötid från en annan sajt?",
          en: "Can I transfer queue time from another site?",
        },
        answer: [
          {
            sv: "Nej. Kötid tillhör alltid det enskilda bostadsföretagets kö och kan inte flyttas mellan företag eller plattformar.",
            en: "No. Queue time always belongs to the individual housing company's queue and cannot be moved between companies or platforms.",
          },
          {
            sv: "Köerna du ser här är företagens egna köer. Har du sedan tidigare kötid hos ett företag är det den som gäller hos dem — CampusLyan varken nollställer eller ändrar den, utan hämtar in den när du går med via oss.",
            en: "The queues you see here are the companies' own queues. If you already have queue time with a company, that is what applies with them — CampusLyan neither resets nor changes it, but picks it up when you join through us.",
          },
        ],
      },
      {
        id: "notiser",
        question: {
          sv: "Hur vet jag om något händer i mina köer?",
          en: "How do I know if something happens in my queues?",
        },
        answer: [
          {
            sv: "Under Notiser samlar vi uppdateringar, till exempel när din köplats ändras eller när en annons du följer byter status. Notiser finns för verifierade studentkonton.",
            en: "Notifications gathers updates, for example when your queue position changes or when a listing you follow changes status. Notifications are available for verified student accounts.",
          },
        ],
        links: [{ href: "/notifications", sv: "Notiser", en: "Notifications" }],
      },
    ],
  },
  {
    id: "ansokningar",
    title: { sv: "Ansökningar och erbjudanden", en: "Applications and offers" },
    description: {
      sv: "Från intresseanmälan till erbjudande — och vad statusarna betyder.",
      en: "From application to offer — and what the statuses mean.",
    },
    items: [
      {
        id: "hur-ansoker-jag",
        question: { sv: "Hur ansöker jag om en bostad?", en: "How do I apply for a home?" },
        answer: [
          {
            sv: "Öppna annonsen när du är inloggad och verifierad och klicka på ansökningsknappen. Ansökan går direkt till bostadsföretaget och dyker upp under Ansökningar, där du följer den hela vägen.",
            en: "Open the listing while logged in and verified and click the apply button. The application goes straight to the housing company and appears under Applications, where you follow it all the way.",
          },
        ],
        links: [
          { href: "/housing", sv: "Sök bostäder", en: "Search homes" },
          { href: "/applications", sv: "Mina ansökningar", en: "My applications" },
        ],
      },
      {
        id: "maste-sta-i-ko",
        question: {
          sv: "Varför står det att jag måste stå i företagets kö?",
          en: "Why does it say I need to be in the company's queue?",
        },
        answer: [
          {
            sv: "Kravet hör till den enskilda bostaden, inte till företaget. Kräver annonsen bostadskö står det på den — då går du med i företagets kö först, och kan sedan ansöka som vanligt. Knappen för att gå med finns direkt på annonsen.",
            en: "The requirement belongs to the individual home, not to the company. If the listing requires a housing queue it says so — join the company's queue first, then apply as usual. The button to join is on the listing itself.",
          },
          {
            sv: "Samma företag kan mycket väl ha andra bostäder som är Express. Är annonsen märkt Express behöver du ingen kö alls för just den.",
            en: "The same company may well have other homes that are Express. If the listing carries the Express mark, you need no queue at all for that one.",
          },
        ],
        links: [{ href: "/all-queues", sv: "Hitta företagets kö", en: "Find the company's queue" }],
      },
      {
        id: "ansokningstak",
        question: {
          sv: "Varför får jag inte söka fler bostäder hos ett företag?",
          en: "Why can't I apply for more homes with a company?",
        },
        answer: [
          {
            sv: "Många bostadsföretag har ett tak för hur många ansökningar du får ha igång samtidigt hos dem. Har du nått taket behöver du dra tillbaka en av dina pågående ansökningar för att kunna söka en ny bostad.",
            en: "Many housing companies cap how many applications you may have running with them at once. If you have reached the cap you need to withdraw one of your ongoing applications before you can apply for a new home.",
          },
          {
            sv: "Taket räknar även ansökningar du gjort direkt i företagets eget system. Sådana visas längst ner på Ansökningar så att siffrorna stämmer, även om vi inte kan hantera dem åt dig.",
            en: "The cap also counts applications you made directly in the company's own system. Those are listed at the bottom of Applications so the numbers add up, even though we cannot manage them for you.",
          },
        ],
      },
      {
        id: "statusar",
        question: {
          sv: "Vad betyder statusarna på mina ansökningar?",
          en: "What do the statuses on my applications mean?",
        },
        answer: [
          {
            sv: "Under granskning betyder att ansökan ligger hos bostadsföretaget. Erbjudande betyder att du fått ett bostadserbjudande att ta ställning till. Antagen betyder att erbjudandet är accepterat. Nekad betyder att företaget gick vidare med någon annan.",
            en: "Under review means the application is with the housing company. Offer means you have received an offer to respond to. Accepted means the offer has been accepted. Rejected means the company went ahead with someone else.",
          },
        ],
      },
      {
        id: "fatt-erbjudande",
        question: { sv: "Jag har fått ett erbjudande — vad händer nu?", en: "I have received an offer — what happens now?" },
        answer: [
          {
            sv: "Öppna Ansökningar och välj att acceptera eller neka erbjudandet. Accepterar du tar bostadsföretaget över och kontaktar dig om kontrakt, visning och inflytt.",
            en: "Open Applications and choose to accept or reject the offer. If you accept, the housing company takes over and contacts you about the contract, viewing and move-in.",
          },
          {
            sv: "Erbjudanden har normalt en svarstid hos företaget, så vänta inte för länge med att svara.",
            en: "Offers normally come with a response deadline set by the company, so do not wait too long to answer.",
          },
        ],
        links: [{ href: "/applications", sv: "Mina ansökningar", en: "My applications" }],
      },
      {
        id: "dra-tillbaka",
        question: { sv: "Kan jag dra tillbaka en ansökan?", en: "Can I withdraw an application?" },
        answer: [
          {
            sv: "Ja, direkt från Ansökningar. Ansökan tas bort hos bostadsföretaget och frigör en plats om företaget har ett ansökningstak.",
            en: "Yes, straight from Applications. The application is removed at the housing company and frees up a slot if the company has an application cap.",
          },
          {
            sv: "Din kötid påverkas inte av att du drar tillbaka en ansökan.",
            en: "Withdrawing an application does not affect your queue time.",
          },
        ],
      },
      {
        id: "vem-bestammer",
        question: { sv: "Vem bestämmer vem som får bostaden?", en: "Who decides who gets the home?" },
        answer: [
          {
            sv: "Alltid bostadsföretaget eller hyresvärden. CampusLyan förmedlar ansökan och informationen, men vi rangordnar inte sökande och kan inte påverka beslutet.",
            en: "Always the housing company or the landlord. CampusLyan passes on the application and the information, but we do not rank applicants and cannot influence the decision.",
          },
        ],
      },
      {
        id: "externt-system",
        question: {
          sv: "Jag får ett fel om att företagets system inte går att nå.",
          en: "I get an error saying the company's system cannot be reached.",
        },
        answer: [
          {
            sv: "Vissa bostadsföretag har sina ansökningar i sitt eget system, och ibland svarar det inte. Det är inget fel på ditt konto och du har inte gjort något fel — vänta en liten stund och försök igen.",
            en: "Some housing companies keep their applications in their own system, and sometimes it does not respond. There is nothing wrong with your account and you have not done anything wrong — wait a little while and try again.",
          },
        ],
      },
    ],
  },
  {
    id: "hitta-bostad",
    title: { sv: "Hitta bostad", en: "Finding a home" },
    description: {
      sv: "Sök, filtrera, spara — och vad du bör läsa innan du ansöker.",
      en: "Search, filter, save — and what to read before you apply.",
    },
    items: [
      {
        id: "hur-soker-jag",
        question: { sv: "Hur söker jag bostäder?", en: "How do I search for homes?" },
        answer: [
          {
            sv: "Gå till Bostäder och filtrera på stad, boendetyp (lägenhet, rum eller korridorsrum), hyra, storlek, antal rum, typ av hyresvärd och bekvämligheter.",
            en: "Go to Housing and filter by city, home type (apartment, room or corridor room), rent, size, number of rooms, landlord type and amenities.",
          },
          {
            sv: "Du kan också utgå från ett lärosäte och sätta ett maxavstånd — smidigt om du vill ha gångavstånd till campus. Träffarna kan visas som lista eller på karta.",
            en: "You can also start from a university and set a maximum distance — handy if you want walking distance to campus. Results can be shown as a list or on a map.",
          },
        ],
        links: [
          { href: "/housing", sv: "Sök bostäder", en: "Search homes" },
          { href: "/cities", sv: "Utforska städer", en: "Explore cities" },
        ],
      },
      {
        id: "varifran-annonser",
        question: { sv: "Varifrån kommer annonserna?", en: "Where do the listings come from?" },
        answer: [
          {
            sv: "Bostadsinformationen kommer från CampusLyan, anslutna bostadsföretag, fastighetsägare och andra källor. På varje annons ser du vem som står bakom bostaden och kan öppna företagets profil.",
            en: "The housing information comes from CampusLyan, connected housing companies, property owners and other sources. Every listing shows who is behind the home and lets you open the company's profile.",
          },
        ],
        links: [{ href: "/partners", sv: "Våra partners", en: "Our partners" }],
      },
      {
        id: "vad-titta-efter",
        question: {
          sv: "Vad ska jag titta efter i en annons?",
          en: "What should I look for in a listing?",
        },
        answer: [
          {
            sv: "Utöver hyra, storlek och läge: läs kravprofilen. Där står vilka krav bostadsföretaget ställer, till exempel åldersspann, och vilka dokument du behöver kunna visa upp.",
            en: "Beyond rent, size and location: read the requirements profile. It states the requirements the housing company sets, such as an age range, and which documents you need to be able to show.",
          },
          {
            sv: "Annonsen visar också vem som är hyresvärd och länkar vidare till företagets egna villkor och integritetspolicy.",
            en: "The listing also shows who the landlord is and links on to the company's own terms and privacy policy.",
          },
        ],
      },
      {
        id: "spara-bostader",
        question: { sv: "Kan jag spara bostäder jag gillar?", en: "Can I save homes I like?" },
        answer: [
          {
            sv: "Ja — klicka på hjärtat på en annons så hamnar den under Sparade, där du kan jämföra dem i lista eller på karta. Funktionen kräver ett verifierat studentkonto.",
            en: "Yes — click the heart on a listing and it lands under Saved, where you can compare them in a list or on a map. The feature requires a verified student account.",
          },
        ],
        links: [{ href: "/saved", sv: "Sparade bostäder", en: "Saved homes" }],
      },
      {
        id: "vilka-stader",
        question: { sv: "Vilka städer täcker ni?", en: "Which cities do you cover?" },
        answer: [
          {
            sv: "Vi fokuserar på Sveriges studentstäder, och utbudet växer löpande i takt med att fler bostadsföretag ansluter sig. Under Städer ser du var vi har bostäder och köer just nu.",
            en: "We focus on Sweden's student cities, and the supply keeps growing as more housing companies join. Under Cities you see where we have homes and queues right now.",
          },
        ],
        links: [{ href: "/cities", sv: "Se alla städer", en: "See all cities" }],
      },
      {
        id: "saknar-stad",
        question: {
          sv: "Jag hittar inte min stad eller mitt lärosäte.",
          en: "I cannot find my city or university.",
        },
        answer: [
          {
            sv: `Då har vi troligen inga anslutna bostadsföretag där ännu. Mejla ${supportEmail} och berätta vilken stad eller vilket lärosäte du saknar — vi prioriterar utifrån var studenterna faktiskt finns.`,
            en: `Then we probably do not have any connected housing companies there yet. Email ${supportEmail} and tell us which city or university you are missing — we prioritise based on where students actually are.`,
          },
        ],
      },
    ],
  },
  {
    id: "dokument-och-trygghet",
    title: { sv: "Dokument, integritet och trygghet", en: "Documents, privacy and safety" },
    description: {
      sv: "Vad du laddar upp, vem som ser det och hur du undviker bluffannonser.",
      en: "What you upload, who can see it and how to avoid scam listings.",
    },
    items: [
      {
        id: "varfor-dokument",
        question: { sv: "Varför ska jag ladda upp dokument?", en: "Why should I upload documents?" },
        answer: [
          {
            sv: "Bostadsföretag ber ofta om studieintyg, antagningsbesked, inkomstunderlag eller liknande. Har du dem sparade under Mitt konto slipper du leta fram dem varje gång du söker en bostad.",
            en: "Housing companies often ask for proof of studies, admission decisions, income documentation or similar. With them stored in My account you avoid digging them out every time you apply for a home.",
          },
          {
            sv: "Du kan ladda upp PDF, Word-filer och bilder (PNG eller JPG), max 20 MB per fil. Dokumentfunktionen kräver ett verifierat studentkonto.",
            en: "You can upload PDFs, Word files and images (PNG or JPG), up to 20 MB per file. The document feature requires a verified student account.",
          },
        ],
        links: [{ href: "/account", sv: "Mina dokument", en: "My documents" }],
      },
      {
        id: "vem-ser-dokument",
        question: { sv: "Vem kan se mina dokument?", en: "Who can see my documents?" },
        answer: [
          {
            sv: "Du själv och de bostadsföretag dokumentet har delats med. I listan ser du vilka företag varje dokument är delat med, och du kan ta bort ett dokument när du vill.",
            en: "You and the housing companies the document has been shared with. The list shows which companies each document is shared with, and you can delete a document whenever you like.",
          },
        ],
      },
      {
        id: "personuppgifter",
        question: {
          sv: "Hur hanterar ni mina personuppgifter?",
          en: "How do you handle my personal data?",
        },
        answer: [
          {
            sv: "Vi behandlar personuppgifter enligt GDPR och sparar dem bara så länge de behövs för ändamålet. Du har bland annat rätt till registerutdrag, rättelse och radering.",
            en: "We process personal data under the GDPR and keep it only for as long as it is needed for the purpose. You have the right to, among other things, access, rectification and erasure.",
          },
          {
            sv: `Detaljerna står i integritetspolicyn. Frågor som rör personuppgifter mejlar du till ${privacyEmail}.`,
            en: `The details are in the privacy policy. Questions about personal data go to ${privacyEmail}.`,
          },
        ],
        links: [
          { href: "/privacy-policy", sv: "Integritetspolicy", en: "Privacy policy" },
          { href: "/cookie-policy", sv: "Cookiepolicy", en: "Cookie policy" },
        ],
      },
      {
        id: "serios-annons",
        question: { sv: "Hur vet jag att en annons är seriös?", en: "How do I know a listing is legitimate?" },
        answer: [
          {
            sv: "Annonserna kommer från bostadsföretag och hyresvärdar som är anslutna till plattformen, och företagsprofilen visar vem som står bakom. CampusLyan ber dig aldrig betala deposition eller hyra via oss.",
            en: "Listings come from housing companies and landlords connected to the platform, and the company profile shows who is behind them. CampusLyan never asks you to pay a deposit or rent through us.",
          },
          {
            sv: `En seriös hyresvärd kräver inte betalning för att du ska få se en bostad, och ber dig inte flytta samtalet till Swish eller privata konton. Känns något fel — mejla ${supportEmail} så tittar vi på det.`,
            en: `A legitimate landlord does not demand payment for you to view a home, and does not ask you to move the conversation to instant payments or private accounts. If something feels off, email ${supportEmail} and we will look into it.`,
          },
        ],
      },
    ],
  },
  {
    id: "om-campuslyan",
    title: { sv: "Om CampusLyan", en: "About CampusLyan" },
    description: {
      sv: "Vilka vi är, vad vi ansvarar för och vad som är på väg.",
      en: "Who we are, what we are responsible for and what is coming.",
    },
    items: [
      {
        id: "ar-ni-hyresvard",
        question: {
          sv: "Är CampusLyan en hyresvärd eller bostadsförmedling?",
          en: "Is CampusLyan a landlord or a letting agency?",
        },
        answer: [
          {
            sv: "Nej. CampusLyan Nordics AB tillhandahåller en teknisk plattform och marknadsplats. Vi är inte hyresvärd, inte bostadsförmedling i juridisk mening, inte ombud för bostadsaktörer och inte part i något hyresavtal.",
            en: "No. CampusLyan Nordics AB provides a technical platform and marketplace. We are not a landlord, not a letting agency in the legal sense, not a representative of housing providers and not a party to any rental agreement.",
          },
          {
            sv: "Hyresavtalet tecknar du alltid direkt med bostadsföretaget eller hyresvärden.",
            en: "You always sign the rental agreement directly with the housing company or the landlord.",
          },
        ],
        links: [
          { href: "/about-us", sv: "Om oss", en: "About us" },
          { href: "/terms-of-service", sv: "Användarvillkor", en: "Terms of use" },
        ],
      },
      {
        id: "sprak",
        question: { sv: "Finns CampusLyan på engelska?", en: "Is CampusLyan available in English?" },
        answer: [
          {
            sv: "Ja. Byt språk med språkväljaren i menyn eller längst ner i sidfoten, så följer valet med dig mellan sidorna.",
            en: "Yes. Switch language with the language picker in the menu or at the bottom of the footer, and the choice follows you between pages.",
          },
        ],
      },
      {
        id: "pa-vag",
        question: { sv: "Vad är på väg?", en: "What is coming next?" },
        answer: [
          {
            sv: "Vi bygger vidare löpande. Närmast står en kunskapsbank med guider om köer, ansökningar och dokument, studenterbjudanden från våra partners, och verifiering med BankID.",
            en: "We keep building. Next up are a knowledge base with guides on queues, applications and documents, student offers from our partners, and verification with BankID.",
          },
        ],
        links: [
          { href: "/knowledge-bank", sv: "Kunskapsbank", en: "Knowledge base" },
          { href: "/offers", sv: "Erbjudanden", en: "Offers" },
        ],
      },
      {
        id: "foretag",
        question: {
          sv: "Jag representerar ett bostadsföretag — hur går vi med?",
          en: "I represent a housing company — how do we join?",
        },
        answer: [
          {
            sv: "Läs mer på För företag och boka en genomgång med oss där. Företagskonton hanteras i vår företagsportal, inte här på studentsidan.",
            en: "Read more on For companies and book a walkthrough with us there. Company accounts are managed in our business portal, not here on the student side.",
          },
        ],
        links: [{ href: "/for-business", sv: "För företag", en: "For companies" }],
      },
      {
        id: "kontakt",
        question: { sv: "Hur får jag kontakt med er?", en: "How do I get in touch with you?" },
        answer: [
          {
            sv: `Mejla ${supportEmail} med din fråga. Beskriv gärna vad du försökte göra och vilken e-postadress kontot har, så går det snabbare att hjälpa dig.`,
            en: `Email ${supportEmail} with your question. Describe what you were trying to do and which email address the account uses, and we can help you faster.`,
          },
          {
            sv: `Frågor om personuppgifter, registerutdrag och radering går till ${privacyEmail}.`,
            en: `Questions about personal data, data access requests and erasure go to ${privacyEmail}.`,
          },
        ],
      },
    ],
  },
];

export function getFaqCategories(locale: Locale): FaqCategory[] {
  return faqSource.map((category) => ({
    id: category.id,
    title: localizedText(locale, category.title.sv, category.title.en),
    description: localizedText(locale, category.description.sv, category.description.en),
    items: category.items.map((item) => ({
      id: item.id,
      question: localizedText(locale, item.question.sv, item.question.en),
      answer: item.answer.map((paragraph) =>
        localizedText(locale, paragraph.sv, paragraph.en)
      ),
      links: (item.links ?? []).map((link) => ({
        href: link.href,
        label: localizedText(locale, link.sv, link.en),
      })),
    })),
  }));
}

/**
 * Platt fråga/svar-lista för FAQPage-JSON-LD:n, som inte känner till
 * kategorier. Styckena slås ihop så att hela svaret indexeras.
 */
export function getFaqJsonLdItems(locale: Locale) {
  return getFaqCategories(locale).flatMap((category) =>
    category.items.map((item) => ({
      question: item.question,
      answer: item.answer.join(" "),
    }))
  );
}
